import { Card, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import { INCIDENCE_STAT_LIST } from '../utils/incidenceStats';

const I18N_BASE = 'scenes.route_report.components.incidences';

/**
 * Anchos variables por barra del ranking, igual que en
 * `RouteTimelineSkeleton`: todas iguales se lee como una tabla vacía en vez
 * de contenido en camino.
 */
const RANKING_BAR_WIDTHS = [78, 46, 28];
const LIST_ROWS = 3;

/**
 * Copia las medidas de `RouteReportIncidences` para que al llegar los datos
 * la card no cambie de alto.
 *
 * El título y las etiquetas de los mini KPIs son fijos y ya se conocen antes
 * de pedir nada, así que van de verdad, no en gris — difuminar lo que ya se
 * sabe hace parpadear la pantalla sin motivo. Los números y sus líneas de
 * apoyo sí dependen de la respuesta, y van difuminados dentro de las mismas
 * cajas (`__stat`) que usa la card real.
 */
function RouteReportIncidencesSkeleton() {
  const { t } = useTranslation();

  return (
    <Card
      className="route-report-incidences"
      // Mismo `title` de `Card` que la card real: si acá se simulara la
      // cabecera dentro del body, el salto de alto al llegar los datos reales
      // sería justo lo que este componente existe para evitar.
      title={
        <div className="route-timeline__head">
          <span className="route-timeline__title">
            {t(`${I18N_BASE}.title`)}
          </span>
        </div>
      }
    >
      <div className="route-report-incidences__body">
        <div className="route-report-incidences__stats">
          {INCIDENCE_STAT_LIST.map((stat) => (
            <div
              className={`route-report-incidences__stat route-report-incidences__stat--${stat.modifier}`}
              key={stat.modifier}
            >
              <span className="route-report-incidences__stat-label">
                {t(`${I18N_BASE}.stats.${stat.i18nKey}.label`)}
              </span>
              <span className="route-report-incidences__stat-value">
                <Skeleton.Input
                  active
                  style={{ width: 48, height: 28, minWidth: 0 }}
                />
              </span>
              <span className="route-report-incidences__stat-sub">
                <Skeleton.Input
                  active
                  style={{ width: 104, height: 12, minWidth: 0 }}
                />
              </span>
            </div>
          ))}
        </div>

        <div className="route-report-incidences__ranking">
          {RANKING_BAR_WIDTHS.map((width, index) => (
            <div className="route-report-incidences__ranking-item" key={index}>
              <Skeleton.Input
                active
                style={{ width: 96, height: 13, minWidth: 0 }}
              />
              <div className="route-report-incidences__ranking-track">
                <Skeleton.Input
                  active
                  block
                  style={{ width: `${width}%`, height: 8, minWidth: 0 }}
                />
              </div>
              <Skeleton.Input
                active
                style={{ width: 20, height: 13, minWidth: 0 }}
              />
            </div>
          ))}
        </div>

        <div className="route-report-incidences__table">
          {/* El encabezado de grilla es texto fijo, como el título: ya se
              conoce antes de pedir nada, así que va de verdad y no en gris
              (T5, punto 2 -- mismas medidas que la card real, incluido este
              renglón nuevo, para que no haya salto al cargar). */}
          <div className="route-report-incidences__list-head">
            <span>{t(`${I18N_BASE}.columns.unit`)}</span>
            <span>{t(`${I18N_BASE}.columns.incidence`)}</span>
          </div>

          <div className="route-report-incidences__list">
            {Array.from({ length: LIST_ROWS }).map((_, index) => (
              <div className="route-report-incidences__row" key={index}>
                <Skeleton.Input
                  active
                  style={{ width: 72, height: 13, minWidth: 0 }}
                />
                <div className="route-report-incidences__row-body">
                  <Skeleton.Input
                    active
                    style={{ width: 168, height: 13, minWidth: 0 }}
                  />
                  <div className="route-report-incidences__row-meta">
                    <Skeleton.Input
                      active
                      style={{ width: 150, height: 12, minWidth: 0 }}
                    />
                    <Skeleton.Input
                      active
                      style={{
                        width: 40,
                        height: 12,
                        minWidth: 0,
                        marginLeft: 'auto',
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default RouteReportIncidencesSkeleton;
